import { useState } from "react";
import { useServer } from "@/lib/server-context";
import { ChevronDown, Check, Plus, Server as ServerIcon } from "lucide-react";

const tierStyles = {
  free: "border-zinc-600 text-zinc-400",
  starter: "border-blue-500 text-blue-400",
  pro: "border-brand-orange text-brand-orange",
  lifetime: "border-yellow-500 text-yellow-400",
  none: "border-red-500 text-red-400",
};

export default function ServerSelector() {
  const { guilds, active, select, botInviteUrl, loading } = useServer();
  const [open, setOpen] = useState(false);

  if (loading) {
    return (
      <div className="px-4 py-3 text-xs font-mono-tabular text-zinc-500">LOADING SERVERS…</div>
    );
  }

  if (!guilds.length) {
    return (
      <div className="p-3" data-testid="no-servers-card">
        <div className="overline mb-2">// NO SERVERS</div>
        <p className="text-xs text-zinc-400 mb-3">
          You don't have any Discord servers where you can manage the bot. Add the bot to your server first.
        </p>
        {botInviteUrl && (
          <a href={botInviteUrl} target="_blank" rel="noreferrer"
             className="btn-primary w-full justify-center text-xs"
             data-testid="add-bot-btn-empty">
            <Plus className="w-3.5 h-3.5" /> Add Bot
          </a>
        )}
      </div>
    );
  }

  return (
    <div className="relative px-3 py-2" data-testid="server-selector">
      <div className="overline mb-2 px-1">// ACTIVE SERVER</div>
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full border border-brand-line hover:border-brand-orange p-3 flex items-center gap-3 transition-colors rounded-lg"
        data-testid="server-selector-toggle"
      >
        {active?.icon ? (
          <img src={active.icon} alt={active.name} className="w-8 h-8 rounded-md object-cover" />
        ) : (
          <div className="w-8 h-8 rounded-md bg-brand-orange/10 border border-brand-orange flex items-center justify-center">
            <ServerIcon className="w-4 h-4 text-brand-orange" />
          </div>
        )}
        <div className="flex-1 min-w-0 text-left">
          <div className="text-sm font-semibold truncate" data-testid="active-guild-name">
            {active?.name || "No server"}
          </div>
          <div className="font-mono-tabular text-[10px] text-zinc-500 uppercase truncate">
            {active?.bot_present ? `TIER · ${active.tier?.toUpperCase()}` : "BOT NOT INSTALLED"}
          </div>
        </div>
        <ChevronDown className={`w-4 h-4 text-zinc-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div
          className="absolute left-3 right-3 mt-2 bg-[#0a0a0a] border border-brand-line rounded-lg shadow-2xl z-50 max-h-[60vh] overflow-y-auto tactical-scroll"
          data-testid="server-selector-dropdown"
        >
          {guilds.map((g) => (
            <button
              key={g.id}
              onClick={() => { select(g); setOpen(false); }}
              className={`w-full p-3 flex items-center gap-3 hover:bg-brand-elevated transition-colors text-left ${
                active?.id === g.id ? "bg-brand-orange/5" : ""
              }`}
              data-testid={`server-option-${g.id}`}
            >
              {g.icon ? (
                <img src={g.icon} alt="" className="w-8 h-8 rounded-md object-cover" />
              ) : (
                <div className="w-8 h-8 rounded-md bg-brand-orange/10 border border-brand-orange flex items-center justify-center">
                  <ServerIcon className="w-4 h-4 text-brand-orange" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate">{g.name}</div>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className={`font-mono-tabular text-[9px] uppercase tracking-wider px-1.5 py-0.5 border rounded ${tierStyles[g.tier] || tierStyles.none}`}>
                    {g.bot_present ? g.tier : "no bot"}
                  </span>
                  {g.owner && <span className="text-[9px] text-yellow-400 font-mono-tabular">OWNER</span>}
                </div>
              </div>
              {active?.id === g.id && <Check className="w-4 h-4 text-brand-orange" />}
            </button>
          ))}
          {botInviteUrl && (
            <a
              href={botInviteUrl}
              target="_blank"
              rel="noreferrer"
              className="block p-3 border-t border-brand-line text-center text-sm font-heading uppercase tracking-wider text-brand-orange hover:bg-brand-orange/10"
              data-testid="add-bot-link"
            >
              <Plus className="w-4 h-4 inline mr-2" />
              Add Bot to Another Server
            </a>
          )}
        </div>
      )}
    </div>
  );
}
